import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { getConsultantData } from '../../../network/apis';
import { updateFormData } from '../../../Store/features/Plans/plansSlice';

export default function ConsltantDataForm({ handlePrev, submitForm, editData, isViewMode, loading_submit }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const [consultantList, setConsultantList] = useState([]);
  const [consultantDropDownOpened, setConsultantDropDownOpened] = useState(false);
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const CustomArrowIcon = ({ isRtl }) => (
    <SvgIcon style={{ transform: 'rotate(-360deg)', color: '#637381' }}>
      <path d="M7 10l5 5 5-5z" />
    </SvgIcon>
  );

  useEffect(() => {
    if (!consultantDropDownOpened) {
      getConsultantList();

      setConsultantDropDownOpened(true);
    }
  }, [editData, consultantDropDownOpened]);

  const submitAllData = (values) => {
    const consultantData = {
      consultant: {
        name: values.name,
        id: values.id,
        website: values.website,
        cr: values.cr,
        email: values.email,
        address: values.address,
      },
    };

    dispatch(updateFormData(consultantData));

    try {
      submitForm();
    } catch (error) {
      console.error('Error calling submitForm:', error);
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('General.required_field')),

    id: Yup.string().required(t('General.required_field')),

    website: Yup.string().required(t('General.required_field')),

    cr: Yup.string().required(t('General.required_field')),

    email: Yup.string().required(t('General.required_field')),

    address: Yup.string().required(t('General.required_field')),
  });
  const getConsultantList = () => {
    setLoading(true);
    getConsultantData()
      .then((res) => {
        setConsultantList(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  return (
    <Formik
      initialValues={{
        ...editData,
        consultant: {
          name: '',
          id: '',
          website: '',
          cr: '',
          email: '',
          address: '',
        },
      }}
      onSubmit={submitAllData}
      validationSchema={validationSchema}
    >
      {({ values, errors, touched, handleChange, handleSubmit, handleBlur, setValues }) => (
        <form onSubmit={handleSubmit}>
          <Grid rowSpacing={2} container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
            <Grid item xs={2} sm={4} md={6}>
              <Stack>
                <InputLabel id="consultantName">{t('consultantForm.consultantName')}</InputLabel>
                <Select
                  sx={{ mt: 1, direction: isRtl ? 'rtl' : 'ltr' }}
                  labelId="id"
                  id="id"
                  value={values.id}
                  disabled={isViewMode}
                  IconComponent={() => <CustomArrowIcon isRtl={isRtl} />}
                  onOpen={() => {
                    if (!consultantDropDownOpened) {
                      getConsultantList();
                      setConsultantDropDownOpened(true);
                    }
                  }}
                  onChange={(event) => {
                    const selectedConsultantId = event.target.value;
                    dispatch(updateFormData({ consultant: { id: selectedConsultantId } }));

                    const selectedConsultant = consultantList.find(
                      (consultant) => consultant.id === selectedConsultantId
                    );
                    if (selectedConsultant) {
                      setValues({
                        ...values,
                        id: selectedConsultant.id,
                        cr: selectedConsultant.cr,
                        name: selectedConsultant.name,
                        website: selectedConsultant.website,
                        email: selectedConsultant.email,
                        phone: selectedConsultant.phone,
                        address: selectedConsultant.address,
                      });
                    }
                  }}
                >
                  {loading ? (
                    <MenuItem>
                      <CircularProgress size={24} />
                    </MenuItem>
                  ) : (
                    consultantList?.map((option, index) => (
                      <MenuItem sx={{ direction: isRtl ? 'rtl' : 'ltr' }} key={index} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </Stack>
            </Grid>

            <Grid item xs={2} sm={4} md={6}>
              <Typography variant="body1" gutterBottom>
                {t('consultantForm.consultantId')}
              </Typography>
              <TextField
                name="id"
                sx={{ direction: isRtl ? 'rtl' : 'ltr' }}
                disabled={isViewMode}
                fullWidth
                value={values.id}
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.id && Boolean(errors.id)}
                helperText={touched.id && errors.id}
              />
            </Grid>

            <Grid item xs={2} sm={4} md={6}>
              <Typography variant="body1" gutterBottom>
                {t('consultantForm.consultantCR')}
              </Typography>
              <TextField
                name="cr"
                sx={{ direction: isRtl ? 'rtl' : 'ltr' }}
                disabled={isViewMode}
                fullWidth
                value={values.cr}
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.cr && Boolean(errors.cr)}
                helperText={touched.cr && errors.cr}
              />
            </Grid>

            <Grid item xs={2} sm={4} md={6}>
              <Typography variant="body1" gutterBottom>
                {t('consultantForm.name')}
              </Typography>
              <TextField
                name="name"
                disabled={isViewMode}
                sx={{ direction: isRtl ? 'rtl' : 'ltr' }}
                fullWidth
                value={values.name}
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && Boolean(errors.name)}
                helperText={
                  touched.name &&
                  errors.name && <span dangerouslySetInnerHTML={{ __html: errors.name.replace('\n', '<br />') }} />
                }
              />
            </Grid>

            <Grid item xs={2} sm={4} md={6}>
              <Typography variant="body1" gutterBottom>
                {t('consultantForm.website')}
              </Typography>
              <TextField
                name="website"
                disabled={isViewMode}
                sx={{ direction: isRtl ? 'rtl' : 'ltr' }}
                fullWidth
                value={values.website}
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.website && Boolean(errors.website)}
                helperText={touched.website && errors.website}
              />
            </Grid>

            <Grid item xs={2} sm={4} md={6}>
              <Typography variant="body1" gutterBottom>
                {t('consultantForm.email')}
              </Typography>
              <TextField
                name="email"
                disabled={isViewMode}
                sx={{ direction: isRtl ? 'rtl' : 'ltr' }}
                fullWidth
                value={values.email}
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />
            </Grid>

            <Grid item xs={2} sm={4} md={6}>
              <Typography variant="body1" gutterBottom>
                {t('consultantForm.address')}
              </Typography>
              <TextField
                name="address"
                sx={{ direction: isRtl ? 'rtl' : 'ltr' }}
                disabled={isViewMode}
                fullWidth
                value={values.address}
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.address && Boolean(errors.address)}
                helperText={touched.address && errors.address}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, width: '100%', display: 'flex', gap: 2, alignItems: 'center' }} spacing={2}>
            <Button
              sx={{ border: '1px solid #111827' }}
              size="large"
              type="button"
              onClick={() => {
                const consultantData = {
                  consultant: {
                    name: values.name,
                    id: values.id,
                    website: values.website,
                    cr: values.cr,
                    email: values.email,
                    address: values.address,
                  },
                };
                dispatch(updateFormData(consultantData));
                handlePrev();
              }}
            >
              {t('consultantForm.back')}
            </Button>
            {isViewMode && (
              <LoadingButton
                style={{ padding: '0.8rem' }}
                variant="contained"
                onClick={() => navigate('/dashboard/plans')}
              >
                {t('consultantForm.ok')}
              </LoadingButton>
            )}

            {!isViewMode && (
              <LoadingButton loading={loading_submit} style={{ padding: '0.8rem' }} variant="contained" type="submit">
                {t('consultantForm.submit')}
              </LoadingButton>
            )}
          </Box>
        </form>
      )}
    </Formik>
  );
}
